// src/utilities/authChallenge.js — SOUND-739
//
// WHERE THE BOT CHALLENGE APPLIES. One answer, used by every site that needs it.
//
// This exists because the rule was written down twice and the two copies drifted.
// AuthForm decided whether to RENDER the Turnstile widget with one condition, and
// whether to let a submit THROUGH with another — and the second one was missing
// the invited-user exemption the first one had:
//
//   render:  props.type === "signup" && !props.inviteEmail   ← exempts invites
//   submit:  ["signup", "forgotpass"].includes(props.type)   ← does not
//
// So on /auth/signup?invite=<token> the widget was suppressed, the token ref
// stayed null forever, and the submit gate refused every attempt with "Still
// checking you are human — give it a second and try again." A PERMANENT dead end
// wearing the words of a temporary one: pressing the button again could never
// fix it, and because the block was client-side, nothing ever reached the server.
// No Firebase account, no user document, no log line — the failure was completely
// silent.
//
// It ran for twelve days. Every invitation sent from 2026-08-27 was dead: 0 of 4
// accepted, against 3 of 3 in the thirty days before. Two of the four were
// PARTNER admin invitations (Lott Agency, ADGUARDIANS) that sat unusable for a
// week and then expired unnoticed. It surfaced only because two internal users
// hit it on 2026-09-08 and said something out loud.
//
// The lesson is not "add the missing clause" — it is that a rule duplicated
// across a render and a guard has nothing keeping the two honest. Derive both
// from here and they cannot disagree again.
//
// WHY EACH FORM ANSWERS THE WAY IT DOES
//
//   signup, self-serve  → CHALLENGED. The abused path (SOUND-715). A missing
//                         token is itself the bot tell, so the client must
//                         refuse rather than fail open — the API refuses too
//                         (SOUND-722), and letting the submit through would burn
//                         a Firebase account to reach the same dead end.
//   signup, invited     → EXEMPT. They arrived through a link in mail WE sent to
//                         a specific address, and POST /users independently
//                         refuses a signup whose email does not match the
//                         invitation. Address control is already proven; a
//                         second proof is pure friction on team onboarding, and
//                         bots do not hold invitation UUIDs. sound-api agrees —
//                         routes/users.mjs: `const isSelfServe = !invitationToken`.
//   forgotpass          → CHALLENGED. Makes us send mail to an address the
//                         requester merely typed.
//   signin              → EXEMPT. The API only demands a token for an
//                         UNCONFIRMED address; a returning user is either
//                         confirmed, or routed to the verification panel, which
//                         carries its own widget and its own token.
//   changepass          → EXEMPT. Reached only from a link we already mailed.
//
// Pure and parameterised rather than reading process.env directly, because
// sound-app has NO component-test harness (SOUND-679) — a function like this is
// the only level at which the rule can actually be pinned.

/** Form types that make us SEND MAIL to an address the caller supplied. */
const CHALLENGED_TYPES = ["signup", "forgotpass"];

/**
 * Does the bot challenge apply to this form?
 *
 * Governs BOTH whether the widget renders and whether a missing token blocks
 * the submit. Callers must not re-derive either half — that divergence is the
 * bug this file exists to prevent.
 *
 * @param {object}  args
 * @param {string}  args.type         the AuthForm type: signup | signin | forgotpass | changepass
 * @param {?string} args.inviteEmail  the invited address, once the token has validated; falsy for self-serve
 * @param {boolean} args.configured   whether a Turnstile site key is present (isTurnstileConfigured())
 * @returns {boolean}
 */
export function challengeApplies({ type, inviteEmail, configured } = {}) {
  // No site key means no widget can ever mount, so demanding a token would
  // block every signup outright. This is the switch that keeps the rollout
  // order safe, exactly as the server's TURNSTILE_SECRET_KEY does.
  if (!configured) return false;

  if (!CHALLENGED_TYPES.includes(type)) return false;

  // The clause whose absence was the bug. Only signup can be an invitation;
  // forgotpass has no invited variant.
  if (type === "signup" && inviteEmail) return false;

  return true;
}
